import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  MessageSquare,
  Heart,
  ListChecks,
  Users,
  Save,
  RotateCcw,
  Info,
  Infinity,
  Crown,
  User,
  Shield,
  ScanBarcode,
  CreditCard,
  Baby,
  FolderOpen,
  Bell,
  Thermometer,
  Package,
  Moon,
  Pill,
  StickyNote,
  TrendingUp,
  Palette,
} from 'lucide-react';
import { getSupabaseAdminClient } from '@parentpath/supabase-client';
import toast from 'react-hot-toast';

const supabase = getSupabaseAdminClient();

interface BaseLimitConfig {
  key: string;
  label: string;
  description: string;
  icon: React.ReactNode;
}

interface NumberLimitConfig extends BaseLimitConfig {
  type: 'number';
  unit: string;
  defaultFree: number;
  defaultPremium: number;
}

interface BooleanLimitConfig extends BaseLimitConfig {
  type: 'boolean';
  defaultFree: boolean;
  defaultPremium: boolean;
}

interface SelectLimitConfig extends BaseLimitConfig {
  type: 'select';
  options: { value: string; label: string }[];
  defaultFree: string;
  defaultPremium: string;
}

interface NumberOrOffLimitConfig extends BaseLimitConfig {
  type: 'number_or_off';
  min: number;
  max: number;
  unit: string;
  defaultFree: number; // 0 = off, positive = amount
  defaultPremium: number;
}

type LimitConfig = NumberLimitConfig | BooleanLimitConfig | SelectLimitConfig | NumberOrOffLimitConfig;

// Limit configurations
const LIMIT_CONFIGS: LimitConfig[] = [
  // Number limits
  {
    key: 'ai_questions_limit',
    label: 'KI-Anfragen pro Monat',
    description: 'Maximale Anzahl KI-Anfragen pro Nutzer pro Monat (-1 = unbegrenzt)',
    icon: <MessageSquare className="w-5 h-5 text-emerald-500" />,
    type: 'number',
    unit: 'Anfragen',
    defaultFree: 10,
    defaultPremium: -1,
  },
  {
    key: 'name_swipes_limit',
    label: 'Namen-Swipes pro Tag',
    description: 'Maximale Anzahl Namen-Swipes pro Tag (-1 = unbegrenzt)',
    icon: <Heart className="w-5 h-5 text-rose-500" />,
    type: 'number',
    unit: 'Swipes',
    defaultFree: 50,
    defaultPremium: -1,
  },
  {
    key: 'checklists_limit',
    label: 'Eigene Checklisten',
    description: 'Maximale Anzahl selbst erstellter Checklisten (-1 = unbegrenzt)',
    icon: <ListChecks className="w-5 h-5 text-purple-500" />,
    type: 'number',
    unit: 'Checklisten',
    defaultFree: 3,
    defaultPremium: -1,
  },

  // Boolean limits
  {
    key: 'all_checklist_templates',
    label: 'Alle Checklisten-Vorlagen',
    description: 'Zugriff auf alle Checklisten-Vorlagen (sonst nur freigegebene)',
    icon: <FolderOpen className="w-5 h-5 text-indigo-500" />,
    type: 'boolean',
    defaultFree: false,
    defaultPremium: true,
  },
  {
    key: 'partner_connection',
    label: 'Partner-Verbindung',
    description: 'Kann sich mit einem Partner verbinden',
    icon: <Users className="w-5 h-5 text-blue-500" />,
    type: 'boolean',
    defaultFree: false,
    defaultPremium: true,
  },
  {
    key: 'barcode_scanner',
    label: 'Produktcheck Barcode-Scanner',
    description: 'Kamera-Barcode-Scanner in nativen Apps (iOS/Android)',
    icon: <ScanBarcode className="w-5 h-5 text-orange-500" />,
    type: 'boolean',
    defaultFree: false,
    defaultPremium: true,
  },
  {
    key: 'pregnancy_card',
    label: 'Schwangerschafts-Infokarte',
    description: 'Zugriff auf die digitale Schwangerschafts-Infokarte',
    icon: <CreditCard className="w-5 h-5 text-pink-500" />,
    type: 'boolean',
    defaultFree: false,
    defaultPremium: true,
  },
  {
    key: 'custom_colors',
    label: 'Benutzerdefiniertes Farbschema',
    description: 'Kann die drei Hauptfarben der App individuell anpassen',
    icon: <Palette className="w-5 h-5 text-violet-500" />,
    type: 'boolean',
    defaultFree: false,
    defaultPremium: true,
  },

  // Select limits
  {
    key: 'contraction_partner_notifications',
    label: 'Wehenzähler Partner-Benachrichtigungen',
    description: 'Welche Benachrichtigungen der Partner bei Wehen erhält',
    icon: <Bell className="w-5 h-5 text-red-500" />,
    type: 'select',
    options: [
      { value: 'off', label: 'Aus' },
      { value: 'important', label: 'Wichtiges (inkl. Statistik)' },
      { value: 'all', label: 'Alle Benachrichtigungen' },
    ],
    defaultFree: 'off',
    defaultPremium: 'all',
  },

  // Number or off limits
  {
    key: 'baby_names_assign',
    label: 'Babynamen vergeben',
    description: 'Anzahl der Namen, die dem Baby zugewiesen werden können (0 = nicht verfügbar)',
    icon: <Baby className="w-5 h-5 text-teal-500" />,
    type: 'number_or_off',
    min: 1,
    max: 5,
    unit: 'Namen',
    defaultFree: 0,
    defaultPremium: 3,
  },
];

// Baby Care Configuration (separate grouped card)
interface BabyCareConfig {
  // History days limit
  history: {
    key: 'baby_care_history_days';
    label: string;
    description: string;
    defaultFree: number;
    defaultPremium: number;
  };
  // Stats max days limit (1=today, 7, 14, 30, 180=individual)
  statsMaxDays: {
    key: 'baby_care_stats_max_days';
    label: string;
    description: string;
    options: { value: number; label: string }[];
    defaultFree: number;
    defaultPremium: number;
  };
  // Care type toggles
  careTypes: {
    key: string;
    label: string;
    icon: React.ReactNode;
    defaultFree: boolean;
    defaultPremium: boolean;
  }[];
  // Sleep Plan limits
  sleepPlan: {
    maxChildren: {
      key: string;
      label: string;
      description: string;
      min: number;
      max: number;
      unit: string;
      defaultFree: number;
      defaultPremium: number;
    };
    analysisDays: {
      key: string;
      label: string;
      description: string;
      unit: string;
      defaultFree: number;
      defaultPremium: number;
    };
    notifications: {
      key: string;
      label: string;
      description: string;
      defaultFree: boolean;
      defaultPremium: boolean;
    };
    patterns: {
      key: string;
      label: string;
      description: string;
      defaultFree: boolean;
      defaultPremium: boolean;
    };
  };
  // Growth entries
  growthEntries: {
    key: string;
    label: string;
    description: string;
    unit: string;
    defaultFree: number;
    defaultPremium: number;
  };
}

const BABY_CARE_CONFIG: BabyCareConfig = {
  history: {
    key: 'baby_care_history_days',
    label: 'Verlauf-Dauer',
    description: 'Wie viele Tage der Verlauf sichtbar ist (-1 = unbegrenzt)',
    defaultFree: 7,
    defaultPremium: -1,
  },
  statsMaxDays: {
    key: 'baby_care_stats_max_days',
    label: 'Statistik-Zeitraum',
    description: 'Maximaler Zeitraum für Statistik-Auswertungen',
    options: [
      { value: 1, label: 'Heute' },
      { value: 7, label: '7 Tage' },
      { value: 14, label: '14 Tage' },
      { value: 30, label: '30 Tage' },
      { value: 180, label: 'Individuell (max. 6 Monate)' },
    ],
    defaultFree: 1,
    defaultPremium: 180,
  },
  careTypes: [
    { key: 'baby_care_feeding', label: 'Füttern', icon: <Baby className="w-4 h-4 text-pink-500" />, defaultFree: true, defaultPremium: true },
    { key: 'baby_care_diaper', label: 'Windeln', icon: <Package className="w-4 h-4 text-amber-500" />, defaultFree: true, defaultPremium: true },
    { key: 'baby_care_sleep', label: 'Schlafen', icon: <Moon className="w-4 h-4 text-indigo-500" />, defaultFree: true, defaultPremium: true },
    { key: 'baby_care_medication', label: 'Medikamente', icon: <Pill className="w-4 h-4 text-red-500" />, defaultFree: false, defaultPremium: true },
    { key: 'baby_care_temperature', label: 'Temperatur', icon: <Thermometer className="w-4 h-4 text-emerald-500" />, defaultFree: false, defaultPremium: true },
    { key: 'baby_care_notes', label: 'Notizen', icon: <StickyNote className="w-4 h-4 text-blue-500" />, defaultFree: false, defaultPremium: true },
  ],
  sleepPlan: {
    maxChildren: {
      key: 'sleep_plan_max_children',
      label: 'Anzahl Kinder',
      description: 'Maximale Anzahl Kinder für den Schlafplan (0 = nicht verfügbar)',
      min: 1,
      max: 5,
      unit: 'Kinder',
      defaultFree: 0,
      defaultPremium: -1,
    },
    analysisDays: {
      key: 'sleep_plan_analysis_days',
      label: 'Analyse-Zeitraum',
      description: 'Maximaler Zeitraum für Schlaf-Analyse in Tagen',
      unit: 'Tage',
      defaultFree: 1,
      defaultPremium: 30,
    },
    notifications: {
      key: 'sleep_plan_notifications',
      label: 'Benachrichtigungen',
      description: 'Wake-Window-Benachrichtigungen',
      defaultFree: true,
      defaultPremium: true,
    },
    patterns: {
      key: 'sleep_plan_patterns',
      label: 'Muster-Erkennung',
      description: 'Personalisierte Schlafmuster-Analyse',
      defaultFree: false,
      defaultPremium: true,
    },
  },
  growthEntries: {
    key: 'baby_growth_entries_max',
    label: 'Wachstums-Messungen',
    description: 'Maximale Anzahl Wachstums-Messungen (-1 = unbegrenzt)',
    unit: 'Messungen',
    defaultFree: 3,
    defaultPremium: -1,
  },
};

// Number Limit Input Component
interface NumberLimitInputProps {
  value: number;
  onChange: (value: number) => void;
  unit: string;
}

function NumberLimitInput({ value, onChange, unit }: NumberLimitInputProps) {
  const isUnlimited = value === -1;
  const [inputValue, setInputValue] = useState(isUnlimited ? '' : String(value));

  useEffect(() => {
    setInputValue(value === -1 ? '' : String(value));
  }, [value]);

  const handleToggleUnlimited = () => {
    if (isUnlimited) {
      onChange(10);
    } else {
      onChange(-1);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputValue(val);
    const numVal = parseInt(val);
    if (!isNaN(numVal) && numVal >= 0) {
      onChange(numVal);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={handleToggleUnlimited}
        className={`px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1.5 transition-colors ${
          isUnlimited
            ? 'bg-teal-100 text-teal-700 border-2 border-teal-500'
            : 'bg-slate-100 text-slate-600 border-2 border-transparent hover:bg-slate-200'
        }`}
      >
        <Infinity className="w-4 h-4" />
        Unbegrenzt
      </button>
      {!isUnlimited && (
        <div className="flex items-center gap-2">
          <input
            type="number"
            min="0"
            value={inputValue}
            onChange={handleInputChange}
            className="w-20 px-3 py-1.5 border border-slate-300 rounded-lg text-center focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
          />
          <span className="text-sm text-slate-500">{unit}</span>
        </div>
      )}
    </div>
  );
}

// Boolean Limit Input Component
interface BooleanLimitInputProps {
  value: boolean;
  onChange: (value: boolean) => void;
}

function BooleanLimitInput({ value, onChange }: BooleanLimitInputProps) {
  return (
    <div className="flex items-center gap-2">
      <button
        onClick={() => onChange(true)}
        className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
          value
            ? 'bg-teal-100 text-teal-700 border-2 border-teal-500'
            : 'bg-slate-100 text-slate-600 border-2 border-transparent hover:bg-slate-200'
        }`}
      >
        Ja
      </button>
      <button
        onClick={() => onChange(false)}
        className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
          !value
            ? 'bg-red-100 text-red-700 border-2 border-red-500'
            : 'bg-slate-100 text-slate-600 border-2 border-transparent hover:bg-slate-200'
        }`}
      >
        Nein
      </button>
    </div>
  );
}

// Select Limit Input Component
interface SelectLimitInputProps {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}

function SelectLimitInput({ value, onChange, options }: SelectLimitInputProps) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

// Number or Off Limit Input Component
interface NumberOrOffLimitInputProps {
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  unit: string;
}

function NumberOrOffLimitInput({ value, onChange, min, max, unit }: NumberOrOffLimitInputProps) {
  const isOff = value === 0;
  const isUnlimited = value === -1;

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={() => onChange(isOff ? min : 0)}
        className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
          isOff
            ? 'bg-red-100 text-red-700 border-2 border-red-500'
            : 'bg-slate-100 text-slate-600 border-2 border-transparent hover:bg-slate-200'
        }`}
      >
        Nein
      </button>
      {!isOff && (
        <>
          <button
            onClick={() => onChange(isUnlimited ? min : -1)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1.5 transition-colors ${
              isUnlimited
                ? 'bg-teal-100 text-teal-700 border-2 border-teal-500'
                : 'bg-slate-100 text-slate-600 border-2 border-transparent hover:bg-slate-200'
            }`}
          >
            <Infinity className="w-4 h-4" />
          </button>
          {!isUnlimited && (
            <div className="flex items-center gap-2">
              <select
                value={value}
                onChange={(e) => onChange(parseInt(e.target.value))}
                className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
              >
                {Array.from({ length: max - min + 1 }, (_, i) => min + i).map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <span className="text-sm text-slate-500">{unit}</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function LimitsPage() {
  const queryClient = useQueryClient();
  const [editedValues, setEditedValues] = useState<Record<string, number | boolean | string>>({});
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch beta user stats
  const { data: betaStats } = useQuery({
    queryKey: ['beta-stats'],
    queryFn: async () => {
      const { count: betaCount } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true })
        .eq('is_beta_tester', true);

      const { count: totalUsers } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true });

      return {
        betaUsers: betaCount || 0,
        totalUsers: totalUsers || 0,
      };
    },
  });

  // Fetch current limit settings
  const { data: settings, isLoading } = useQuery({
    queryKey: ['tier-limit-settings'],
    queryFn: async () => {
      // Include regular limit keys
      const keys = LIMIT_CONFIGS.flatMap((c) => [`${c.key}_free`, `${c.key}_premium`]);

      // Add baby care keys
      const babyCareKeys = [
        `${BABY_CARE_CONFIG.history.key}_free`,
        `${BABY_CARE_CONFIG.history.key}_premium`,
        `${BABY_CARE_CONFIG.statsMaxDays.key}_free`,
        `${BABY_CARE_CONFIG.statsMaxDays.key}_premium`,
        ...BABY_CARE_CONFIG.careTypes.flatMap((c) => [`${c.key}_free`, `${c.key}_premium`]),
        // Sleep Plan
        `${BABY_CARE_CONFIG.sleepPlan.maxChildren.key}_free`,
        `${BABY_CARE_CONFIG.sleepPlan.maxChildren.key}_premium`,
        `${BABY_CARE_CONFIG.sleepPlan.analysisDays.key}_free`,
        `${BABY_CARE_CONFIG.sleepPlan.analysisDays.key}_premium`,
        `${BABY_CARE_CONFIG.sleepPlan.notifications.key}_free`,
        `${BABY_CARE_CONFIG.sleepPlan.notifications.key}_premium`,
        `${BABY_CARE_CONFIG.sleepPlan.patterns.key}_free`,
        `${BABY_CARE_CONFIG.sleepPlan.patterns.key}_premium`,
        // Growth
        `${BABY_CARE_CONFIG.growthEntries.key}_free`,
        `${BABY_CARE_CONFIG.growthEntries.key}_premium`,
      ];

      const allKeys = [...keys, ...babyCareKeys];

      const { data, error } = await supabase
        .from('system_settings')
        .select('key, value')
        .in('key', allKeys);

      if (error) throw error;

      const settingsMap: Record<string, number | boolean | string> = {};
      (data as { key: string; value: unknown }[])?.forEach((s) => {
        settingsMap[s.key] = s.value as number | boolean | string;
      });

      return settingsMap;
    },
  });

  // Initialize edited values when settings load
  useEffect(() => {
    if (settings) {
      const initialValues: Record<string, number | boolean | string> = {};

      // Regular configs
      LIMIT_CONFIGS.forEach((config) => {
        const freeKey = `${config.key}_free`;
        const premiumKey = `${config.key}_premium`;

        if (config.type === 'number' || config.type === 'number_or_off') {
          initialValues[freeKey] = (settings[freeKey] as number) ?? config.defaultFree;
          initialValues[premiumKey] = (settings[premiumKey] as number) ?? config.defaultPremium;
        } else if (config.type === 'boolean') {
          initialValues[freeKey] = (settings[freeKey] as boolean) ?? config.defaultFree;
          initialValues[premiumKey] = (settings[premiumKey] as boolean) ?? config.defaultPremium;
        } else if (config.type === 'select') {
          initialValues[freeKey] = (settings[freeKey] as string) ?? config.defaultFree;
          initialValues[premiumKey] = (settings[premiumKey] as string) ?? config.defaultPremium;
        }
      });

      // Baby Care configs
      const historyFreeKey = `${BABY_CARE_CONFIG.history.key}_free`;
      const historyPremiumKey = `${BABY_CARE_CONFIG.history.key}_premium`;
      initialValues[historyFreeKey] = (settings[historyFreeKey] as number) ?? BABY_CARE_CONFIG.history.defaultFree;
      initialValues[historyPremiumKey] = (settings[historyPremiumKey] as number) ?? BABY_CARE_CONFIG.history.defaultPremium;

      const statsFreeKey = `${BABY_CARE_CONFIG.statsMaxDays.key}_free`;
      const statsPremiumKey = `${BABY_CARE_CONFIG.statsMaxDays.key}_premium`;
      initialValues[statsFreeKey] = (settings[statsFreeKey] as number) ?? BABY_CARE_CONFIG.statsMaxDays.defaultFree;
      initialValues[statsPremiumKey] = (settings[statsPremiumKey] as number) ?? BABY_CARE_CONFIG.statsMaxDays.defaultPremium;

      BABY_CARE_CONFIG.careTypes.forEach((ct) => {
        const freeKey = `${ct.key}_free`;
        const premiumKey = `${ct.key}_premium`;
        initialValues[freeKey] = (settings[freeKey] as boolean) ?? ct.defaultFree;
        initialValues[premiumKey] = (settings[premiumKey] as boolean) ?? ct.defaultPremium;
      });

      // Sleep Plan configs
      const sp = BABY_CARE_CONFIG.sleepPlan;
      initialValues[`${sp.maxChildren.key}_free`] = (settings[`${sp.maxChildren.key}_free`] as number) ?? sp.maxChildren.defaultFree;
      initialValues[`${sp.maxChildren.key}_premium`] = (settings[`${sp.maxChildren.key}_premium`] as number) ?? sp.maxChildren.defaultPremium;
      initialValues[`${sp.analysisDays.key}_free`] = (settings[`${sp.analysisDays.key}_free`] as number) ?? sp.analysisDays.defaultFree;
      initialValues[`${sp.analysisDays.key}_premium`] = (settings[`${sp.analysisDays.key}_premium`] as number) ?? sp.analysisDays.defaultPremium;
      initialValues[`${sp.notifications.key}_free`] = (settings[`${sp.notifications.key}_free`] as boolean) ?? sp.notifications.defaultFree;
      initialValues[`${sp.notifications.key}_premium`] = (settings[`${sp.notifications.key}_premium`] as boolean) ?? sp.notifications.defaultPremium;
      initialValues[`${sp.patterns.key}_free`] = (settings[`${sp.patterns.key}_free`] as boolean) ?? sp.patterns.defaultFree;
      initialValues[`${sp.patterns.key}_premium`] = (settings[`${sp.patterns.key}_premium`] as boolean) ?? sp.patterns.defaultPremium;

      // Growth entries
      const ge = BABY_CARE_CONFIG.growthEntries;
      initialValues[`${ge.key}_free`] = (settings[`${ge.key}_free`] as number) ?? ge.defaultFree;
      initialValues[`${ge.key}_premium`] = (settings[`${ge.key}_premium`] as number) ?? ge.defaultPremium;

      setEditedValues(initialValues);
    }
  }, [settings]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (values: Record<string, number | boolean | string>) => {
      for (const [key, value] of Object.entries(values)) {
        const { error } = await supabase.from('system_settings').upsert(
          {
            key,
            value: value,
            description: `Limit setting: ${key}`,
            updated_at: new Date().toISOString(),
          } as never,
          {
            onConflict: 'key',
          }
        );

        if (error) {
          console.error(`Error saving ${key}:`, error);
          throw error;
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tier-limit-settings'] });
      setHasChanges(false);
      toast.success('Limits gespeichert');
    },
    onError: () => {
      toast.error('Fehler beim Speichern');
    },
  });

  const handleValueChange = (key: string, value: number | boolean | string) => {
    setEditedValues((prev) => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleReset = () => {
    const resetValues: Record<string, number | boolean | string> = {};

    // Regular configs
    LIMIT_CONFIGS.forEach((config) => {
      resetValues[`${config.key}_free`] = config.defaultFree;
      resetValues[`${config.key}_premium`] = config.defaultPremium;
    });

    // Baby Care configs
    resetValues[`${BABY_CARE_CONFIG.history.key}_free`] = BABY_CARE_CONFIG.history.defaultFree;
    resetValues[`${BABY_CARE_CONFIG.history.key}_premium`] = BABY_CARE_CONFIG.history.defaultPremium;
    resetValues[`${BABY_CARE_CONFIG.statsMaxDays.key}_free`] = BABY_CARE_CONFIG.statsMaxDays.defaultFree;
    resetValues[`${BABY_CARE_CONFIG.statsMaxDays.key}_premium`] = BABY_CARE_CONFIG.statsMaxDays.defaultPremium;
    BABY_CARE_CONFIG.careTypes.forEach((ct) => {
      resetValues[`${ct.key}_free`] = ct.defaultFree;
      resetValues[`${ct.key}_premium`] = ct.defaultPremium;
    });

    // Sleep Plan
    const sp = BABY_CARE_CONFIG.sleepPlan;
    resetValues[`${sp.maxChildren.key}_free`] = sp.maxChildren.defaultFree;
    resetValues[`${sp.maxChildren.key}_premium`] = sp.maxChildren.defaultPremium;
    resetValues[`${sp.analysisDays.key}_free`] = sp.analysisDays.defaultFree;
    resetValues[`${sp.analysisDays.key}_premium`] = sp.analysisDays.defaultPremium;
    resetValues[`${sp.notifications.key}_free`] = sp.notifications.defaultFree;
    resetValues[`${sp.notifications.key}_premium`] = sp.notifications.defaultPremium;
    resetValues[`${sp.patterns.key}_free`] = sp.patterns.defaultFree;
    resetValues[`${sp.patterns.key}_premium`] = sp.patterns.defaultPremium;

    // Growth
    const ge = BABY_CARE_CONFIG.growthEntries;
    resetValues[`${ge.key}_free`] = ge.defaultFree;
    resetValues[`${ge.key}_premium`] = ge.defaultPremium;

    setEditedValues(resetValues);
    setHasChanges(true);
  };

  const handleSave = () => {
    saveMutation.mutate(editedValues);
  };

  const getValue = <T extends number | boolean | string>(key: string, defaultValue: T): T => {
    return (editedValues[key] as T) ?? (settings?.[key] as T) ?? defaultValue;
  };

  const formatValue = (config: LimitConfig, value: number | boolean | string): string => {
    if (config.type === 'number') {
      return value === -1 ? '∞' : String(value);
    } else if (config.type === 'boolean') {
      return value ? 'Ja' : 'Nein';
    } else if (config.type === 'select') {
      const option = config.options.find((o) => o.value === value);
      return option?.label || String(value);
    } else if (config.type === 'number_or_off') {
      return value === 0 ? 'Nein' : value === -1 ? '∞' : `${value} ${config.unit}`;
    }
    return String(value);
  };

  // Group configs by type for better organization
  const numberConfigs = LIMIT_CONFIGS.filter((c) => c.type === 'number');
  const booleanConfigs = LIMIT_CONFIGS.filter((c) => c.type === 'boolean');
  const selectConfigs = LIMIT_CONFIGS.filter((c) => c.type === 'select');
  const numberOrOffConfigs = LIMIT_CONFIGS.filter((c) => c.type === 'number_or_off');

  const renderLimitCard = (config: LimitConfig) => {
    const freeKey = `${config.key}_free`;
    const premiumKey = `${config.key}_premium`;

    let freeValue: number | boolean | string;
    let premiumValue: number | boolean | string;

    if (config.type === 'number' || config.type === 'number_or_off') {
      freeValue = getValue<number>(freeKey, config.defaultFree);
      premiumValue = getValue<number>(premiumKey, config.defaultPremium);
    } else if (config.type === 'boolean') {
      freeValue = getValue<boolean>(freeKey, config.defaultFree);
      premiumValue = getValue<boolean>(premiumKey, config.defaultPremium);
    } else {
      freeValue = getValue<string>(freeKey, config.defaultFree);
      premiumValue = getValue<string>(premiumKey, config.defaultPremium);
    }

    return (
      <div key={config.key} className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
            {config.icon}
          </div>
          <div>
            <h3 className="font-medium text-slate-900">{config.label}</h3>
            <p className="text-sm text-slate-500">{config.description}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6 mt-4 pl-14">
          {/* Free Tier */}
          <div className="p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <User className="w-4 h-4 text-slate-600" />
              <span className="font-medium text-slate-700">Free</span>
              <span className="ml-auto text-sm font-bold text-slate-900">
                {formatValue(config, freeValue)}
              </span>
            </div>
            {config.type === 'number' && (
              <NumberLimitInput
                value={freeValue as number}
                onChange={(value) => handleValueChange(freeKey, value)}
                unit={config.unit}
              />
            )}
            {config.type === 'boolean' && (
              <BooleanLimitInput
                value={freeValue as boolean}
                onChange={(value) => handleValueChange(freeKey, value)}
              />
            )}
            {config.type === 'select' && (
              <SelectLimitInput
                value={freeValue as string}
                onChange={(value) => handleValueChange(freeKey, value)}
                options={config.options}
              />
            )}
            {config.type === 'number_or_off' && (
              <NumberOrOffLimitInput
                value={freeValue as number}
                onChange={(value) => handleValueChange(freeKey, value)}
                min={config.min}
                max={config.max}
                unit={config.unit}
              />
            )}
          </div>

          {/* Premium Tier */}
          <div className="p-4 bg-amber-50 rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <Crown className="w-4 h-4 text-amber-600" />
              <span className="font-medium text-amber-700">Premium</span>
              <span className="ml-auto text-sm font-bold text-amber-900">
                {formatValue(config, premiumValue)}
              </span>
            </div>
            {config.type === 'number' && (
              <NumberLimitInput
                value={premiumValue as number}
                onChange={(value) => handleValueChange(premiumKey, value)}
                unit={config.unit}
              />
            )}
            {config.type === 'boolean' && (
              <BooleanLimitInput
                value={premiumValue as boolean}
                onChange={(value) => handleValueChange(premiumKey, value)}
              />
            )}
            {config.type === 'select' && (
              <SelectLimitInput
                value={premiumValue as string}
                onChange={(value) => handleValueChange(premiumKey, value)}
                options={config.options}
              />
            )}
            {config.type === 'number_or_off' && (
              <NumberOrOffLimitInput
                value={premiumValue as number}
                onChange={(value) => handleValueChange(premiumKey, value)}
                min={config.min}
                max={config.max}
                unit={config.unit}
              />
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render Baby Care Grouped Card
  const renderBabyCareCard = () => {
    const historyFreeKey = `${BABY_CARE_CONFIG.history.key}_free`;
    const historyPremiumKey = `${BABY_CARE_CONFIG.history.key}_premium`;
    const statsFreeKey = `${BABY_CARE_CONFIG.statsMaxDays.key}_free`;
    const statsPremiumKey = `${BABY_CARE_CONFIG.statsMaxDays.key}_premium`;

    const historyFreeValue = getValue<number>(historyFreeKey, BABY_CARE_CONFIG.history.defaultFree);
    const historyPremiumValue = getValue<number>(historyPremiumKey, BABY_CARE_CONFIG.history.defaultPremium);
    const statsFreeValue = getValue<number>(statsFreeKey, BABY_CARE_CONFIG.statsMaxDays.defaultFree);
    const statsPremiumValue = getValue<number>(statsPremiumKey, BABY_CARE_CONFIG.statsMaxDays.defaultPremium);

    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        {/* Header */}
        <div className="flex items-start gap-4 mb-6">
          <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center">
            <Baby className="w-5 h-5 text-pink-500" />
          </div>
          <div>
            <h3 className="font-medium text-slate-900">Baby-Care-Koordinator</h3>
            <p className="text-sm text-slate-500">
              Einstellungen für das Baby-Pflege-Tracking nach der Geburt
            </p>
          </div>
        </div>

        {/* Aktionen Section */}
        <div className="mb-6">
          <h4 className="text-sm font-semibold text-slate-700 mb-2">Aktionen</h4>
          <p className="text-xs text-slate-500 mb-4">
            Welche Tracking-Aktionen für den jeweiligen Plan verfügbar sind
          </p>

          <div className="grid grid-cols-2 gap-4">
            {/* Free Tier */}
            <div className="p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-2 mb-4">
                <User className="w-4 h-4 text-slate-600" />
                <span className="font-medium text-slate-700">Free</span>
              </div>
              <div className="space-y-3">
                {BABY_CARE_CONFIG.careTypes.map((ct) => {
                  const freeValue = getValue<boolean>(`${ct.key}_free`, ct.defaultFree);
                  return (
                    <div key={ct.key} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {ct.icon}
                        <span className="text-sm text-slate-700">{ct.label}</span>
                      </div>
                      <button
                        onClick={() => handleValueChange(`${ct.key}_free`, !freeValue)}
                        className={`w-10 h-6 rounded-full transition-colors ${
                          freeValue ? 'bg-teal-500' : 'bg-slate-300'
                        }`}
                      >
                        <div
                          className={`w-4 h-4 bg-white rounded-full shadow transition-transform ${
                            freeValue ? 'translate-x-5' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Premium Tier */}
            <div className="p-4 bg-amber-50 rounded-lg">
              <div className="flex items-center gap-2 mb-4">
                <Crown className="w-4 h-4 text-amber-600" />
                <span className="font-medium text-amber-700">Premium</span>
              </div>
              <div className="space-y-3">
                {BABY_CARE_CONFIG.careTypes.map((ct) => {
                  const premiumValue = getValue<boolean>(`${ct.key}_premium`, ct.defaultPremium);
                  return (
                    <div key={ct.key} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {ct.icon}
                        <span className="text-sm text-amber-700">{ct.label}</span>
                      </div>
                      <button
                        onClick={() => handleValueChange(`${ct.key}_premium`, !premiumValue)}
                        className={`w-10 h-6 rounded-full transition-colors ${
                          premiumValue ? 'bg-teal-500' : 'bg-slate-300'
                        }`}
                      >
                        <div
                          className={`w-4 h-4 bg-white rounded-full shadow transition-transform ${
                            premiumValue ? 'translate-x-5' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-slate-200 my-6" />

        {/* Statistik-Zeitraum Section */}
        <div className="mb-6">
          <h4 className="text-sm font-semibold text-slate-700 mb-2">Statistik-Zeitraum</h4>
          <p className="text-xs text-slate-500 mb-4">
            Maximaler Zeitraum für Statistik-Auswertungen. Niedrigere Optionen sind automatisch inkludiert.
          </p>

          <div className="grid grid-cols-2 gap-4">
            {/* Free Tier */}
            <div className="p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <User className="w-4 h-4 text-slate-600" />
                <span className="font-medium text-slate-700">Free</span>
                <span className="ml-auto text-sm font-bold text-slate-900">
                  {BABY_CARE_CONFIG.statsMaxDays.options.find((o) => o.value === statsFreeValue)?.label || '-'}
                </span>
              </div>
              <select
                value={statsFreeValue}
                onChange={(e) => handleValueChange(statsFreeKey, parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
              >
                {BABY_CARE_CONFIG.statsMaxDays.options.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Premium Tier */}
            <div className="p-4 bg-amber-50 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <Crown className="w-4 h-4 text-amber-600" />
                <span className="font-medium text-amber-700">Premium</span>
                <span className="ml-auto text-sm font-bold text-amber-900">
                  {BABY_CARE_CONFIG.statsMaxDays.options.find((o) => o.value === statsPremiumValue)?.label || '-'}
                </span>
              </div>
              <select
                value={statsPremiumValue}
                onChange={(e) => handleValueChange(statsPremiumKey, parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
              >
                {BABY_CARE_CONFIG.statsMaxDays.options.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-slate-200 my-6" />

        {/* Verlauf-Dauer Section */}
        <div>
          <h4 className="text-sm font-semibold text-slate-700 mb-2">Verlauf-Dauer</h4>
          <p className="text-xs text-slate-500 mb-4">
            Wie lange Einträge im Verlauf sichtbar bleiben
          </p>

          <div className="grid grid-cols-2 gap-4">
            {/* Free Tier */}
            <div className="p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <User className="w-4 h-4 text-slate-600" />
                <span className="font-medium text-slate-700">Free</span>
                <span className="ml-auto text-sm font-bold text-slate-900">
                  {historyFreeValue === -1 ? '∞' : `${historyFreeValue} Tage`}
                </span>
              </div>
              <NumberLimitInput
                value={historyFreeValue}
                onChange={(value) => handleValueChange(historyFreeKey, value)}
                unit="Tage"
              />
            </div>

            {/* Premium Tier */}
            <div className="p-4 bg-amber-50 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <Crown className="w-4 h-4 text-amber-600" />
                <span className="font-medium text-amber-700">Premium</span>
                <span className="ml-auto text-sm font-bold text-amber-900">
                  {historyPremiumValue === -1 ? '∞' : `${historyPremiumValue} Tage`}
                </span>
              </div>
              <NumberLimitInput
                value={historyPremiumValue}
                onChange={(value) => handleValueChange(historyPremiumKey, value)}
                unit="Tage"
              />
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-slate-200 my-6" />

        {/* Wachstums-Messungen Section */}
        <div className="mb-6">
          <h4 className="text-sm font-semibold text-slate-700 mb-2">
            <span className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-emerald-500" />
              {BABY_CARE_CONFIG.growthEntries.label}
            </span>
          </h4>
          <p className="text-xs text-slate-500 mb-4">{BABY_CARE_CONFIG.growthEntries.description}</p>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <User className="w-4 h-4 text-slate-600" />
                <span className="font-medium text-slate-700">Free</span>
                <span className="ml-auto text-sm font-bold text-slate-900">
                  {getValue<number>(`${BABY_CARE_CONFIG.growthEntries.key}_free`, BABY_CARE_CONFIG.growthEntries.defaultFree) === -1
                    ? '∞'
                    : getValue<number>(`${BABY_CARE_CONFIG.growthEntries.key}_free`, BABY_CARE_CONFIG.growthEntries.defaultFree)}
                </span>
              </div>
              <NumberLimitInput
                value={getValue<number>(`${BABY_CARE_CONFIG.growthEntries.key}_free`, BABY_CARE_CONFIG.growthEntries.defaultFree)}
                onChange={(value) => handleValueChange(`${BABY_CARE_CONFIG.growthEntries.key}_free`, value)}
                unit={BABY_CARE_CONFIG.growthEntries.unit}
              />
            </div>
            <div className="p-4 bg-amber-50 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <Crown className="w-4 h-4 text-amber-600" />
                <span className="font-medium text-amber-700">Premium</span>
                <span className="ml-auto text-sm font-bold text-amber-900">
                  {getValue<number>(`${BABY_CARE_CONFIG.growthEntries.key}_premium`, BABY_CARE_CONFIG.growthEntries.defaultPremium) === -1
                    ? '∞'
                    : getValue<number>(`${BABY_CARE_CONFIG.growthEntries.key}_premium`, BABY_CARE_CONFIG.growthEntries.defaultPremium)}
                </span>
              </div>
              <NumberLimitInput
                value={getValue<number>(`${BABY_CARE_CONFIG.growthEntries.key}_premium`, BABY_CARE_CONFIG.growthEntries.defaultPremium)}
                onChange={(value) => handleValueChange(`${BABY_CARE_CONFIG.growthEntries.key}_premium`, value)}
                unit={BABY_CARE_CONFIG.growthEntries.unit}
              />
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-slate-200 my-6" />

        {/* Schlafplan Section */}
        <div>
          <h4 className="text-sm font-semibold text-slate-700 mb-2">
            <span className="flex items-center gap-2">
              <Moon className="w-4 h-4 text-indigo-500" />
              Schlafplan
            </span>
          </h4>
          <p className="text-xs text-slate-500 mb-4">
            Einstellungen für den Wake-Window-basierten Schlafplan
          </p>

          <div className="space-y-4">
            {/* Max Children */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <User className="w-4 h-4 text-slate-600" />
                  <span className="font-medium text-slate-700">Free</span>
                  <span className="ml-auto text-xs text-slate-500">{BABY_CARE_CONFIG.sleepPlan.maxChildren.label}</span>
                </div>
                <NumberOrOffLimitInput
                  value={getValue<number>(`${BABY_CARE_CONFIG.sleepPlan.maxChildren.key}_free`, BABY_CARE_CONFIG.sleepPlan.maxChildren.defaultFree)}
                  onChange={(value) => handleValueChange(`${BABY_CARE_CONFIG.sleepPlan.maxChildren.key}_free`, value)}
                  min={BABY_CARE_CONFIG.sleepPlan.maxChildren.min}
                  max={BABY_CARE_CONFIG.sleepPlan.maxChildren.max}
                  unit={BABY_CARE_CONFIG.sleepPlan.maxChildren.unit}
                />
              </div>
              <div className="p-4 bg-amber-50 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <Crown className="w-4 h-4 text-amber-600" />
                  <span className="font-medium text-amber-700">Premium</span>
                  <span className="ml-auto text-xs text-amber-600">{BABY_CARE_CONFIG.sleepPlan.maxChildren.label}</span>
                </div>
                <NumberOrOffLimitInput
                  value={getValue<number>(`${BABY_CARE_CONFIG.sleepPlan.maxChildren.key}_premium`, BABY_CARE_CONFIG.sleepPlan.maxChildren.defaultPremium)}
                  onChange={(value) => handleValueChange(`${BABY_CARE_CONFIG.sleepPlan.maxChildren.key}_premium`, value)}
                  min={BABY_CARE_CONFIG.sleepPlan.maxChildren.min}
                  max={BABY_CARE_CONFIG.sleepPlan.maxChildren.max}
                  unit={BABY_CARE_CONFIG.sleepPlan.maxChildren.unit}
                />
              </div>
            </div>

            {/* Analysis Days */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <User className="w-4 h-4 text-slate-600" />
                  <span className="font-medium text-slate-700">Free</span>
                  <span className="ml-auto text-xs text-slate-500">{BABY_CARE_CONFIG.sleepPlan.analysisDays.label}</span>
                </div>
                <NumberLimitInput
                  value={getValue<number>(`${BABY_CARE_CONFIG.sleepPlan.analysisDays.key}_free`, BABY_CARE_CONFIG.sleepPlan.analysisDays.defaultFree)}
                  onChange={(value) => handleValueChange(`${BABY_CARE_CONFIG.sleepPlan.analysisDays.key}_free`, value)}
                  unit={BABY_CARE_CONFIG.sleepPlan.analysisDays.unit}
                />
              </div>
              <div className="p-4 bg-amber-50 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <Crown className="w-4 h-4 text-amber-600" />
                  <span className="font-medium text-amber-700">Premium</span>
                  <span className="ml-auto text-xs text-amber-600">{BABY_CARE_CONFIG.sleepPlan.analysisDays.label}</span>
                </div>
                <NumberLimitInput
                  value={getValue<number>(`${BABY_CARE_CONFIG.sleepPlan.analysisDays.key}_premium`, BABY_CARE_CONFIG.sleepPlan.analysisDays.defaultPremium)}
                  onChange={(value) => handleValueChange(`${BABY_CARE_CONFIG.sleepPlan.analysisDays.key}_premium`, value)}
                  unit={BABY_CARE_CONFIG.sleepPlan.analysisDays.unit}
                />
              </div>
            </div>

            {/* Notifications & Patterns */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <User className="w-4 h-4 text-slate-600" />
                  <span className="font-medium text-slate-700">Free</span>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-700">{BABY_CARE_CONFIG.sleepPlan.notifications.label}</span>
                    <BooleanLimitInput
                      value={getValue<boolean>(`${BABY_CARE_CONFIG.sleepPlan.notifications.key}_free`, BABY_CARE_CONFIG.sleepPlan.notifications.defaultFree)}
                      onChange={(value) => handleValueChange(`${BABY_CARE_CONFIG.sleepPlan.notifications.key}_free`, value)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-700">{BABY_CARE_CONFIG.sleepPlan.patterns.label}</span>
                    <BooleanLimitInput
                      value={getValue<boolean>(`${BABY_CARE_CONFIG.sleepPlan.patterns.key}_free`, BABY_CARE_CONFIG.sleepPlan.patterns.defaultFree)}
                      onChange={(value) => handleValueChange(`${BABY_CARE_CONFIG.sleepPlan.patterns.key}_free`, value)}
                    />
                  </div>
                </div>
              </div>
              <div className="p-4 bg-amber-50 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <Crown className="w-4 h-4 text-amber-600" />
                  <span className="font-medium text-amber-700">Premium</span>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-amber-700">{BABY_CARE_CONFIG.sleepPlan.notifications.label}</span>
                    <BooleanLimitInput
                      value={getValue<boolean>(`${BABY_CARE_CONFIG.sleepPlan.notifications.key}_premium`, BABY_CARE_CONFIG.sleepPlan.notifications.defaultPremium)}
                      onChange={(value) => handleValueChange(`${BABY_CARE_CONFIG.sleepPlan.notifications.key}_premium`, value)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-amber-700">{BABY_CARE_CONFIG.sleepPlan.patterns.label}</span>
                    <BooleanLimitInput
                      value={getValue<boolean>(`${BABY_CARE_CONFIG.sleepPlan.patterns.key}_premium`, BABY_CARE_CONFIG.sleepPlan.patterns.defaultPremium)}
                      onChange={(value) => handleValueChange(`${BABY_CARE_CONFIG.sleepPlan.patterns.key}_premium`, value)}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Limits</h1>
            <p className="text-slate-600">Nutzungslimits für Free und Premium User konfigurieren</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleReset}
              className="px-4 py-2 text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg font-medium flex items-center gap-2"
            >
              <RotateCcw className="w-4 h-4" />
              Zurücksetzen
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges || saveMutation.isPending}
              className="px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-lg font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              Speichern
            </button>
          </div>
        </div>
      </div>

      {/* Tier Legend */}
      <div className="flex gap-6 mb-6 p-4 bg-slate-50 rounded-lg">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center">
            <User className="w-4 h-4 text-slate-600" />
          </div>
          <div>
            <div className="font-medium text-slate-900">Free</div>
            <div className="text-xs text-slate-500">Kostenlose Nutzer</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
            <Crown className="w-4 h-4 text-amber-600" />
          </div>
          <div>
            <div className="font-medium text-slate-900">Premium</div>
            <div className="text-xs text-slate-500">Zahlende Nutzer</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
            <Shield className="w-4 h-4 text-purple-600" />
          </div>
          <div>
            <div className="font-medium text-slate-900">Beta</div>
            <div className="text-xs text-slate-500">= Premium Limits</div>
          </div>
        </div>
      </div>

      {/* Beta Info */}
      <div className="mb-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-purple-800">Beta-Tester</h3>
              <span className="px-3 py-1 bg-purple-200 text-purple-800 text-sm font-medium rounded-full">
                {betaStats?.betaUsers || 0} von {betaStats?.totalUsers || 0} Nutzern
              </span>
            </div>
            <p className="text-sm text-purple-700 mt-1">
              Beta-Tester erhalten automatisch <strong>Premium-Limits</strong>. Sie können im
              Benutzer-Detail (Tab &quot;Premium&quot;) aktiviert/deaktiviert werden.
            </p>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-slate-500">Lädt Einstellungen...</div>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Zahlenlimits */}
          <div>
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Infinity className="w-5 h-5 text-slate-400" />
              Zahlenlimits
            </h2>
            <div className="space-y-4">{numberConfigs.map(renderLimitCard)}</div>
          </div>

          {/* Feature-Zugang */}
          <div>
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-slate-400" />
              Feature-Zugang
            </h2>
            <div className="space-y-4">{booleanConfigs.map(renderLimitCard)}</div>
          </div>

          {/* Benachrichtigungen */}
          {selectConfigs.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Bell className="w-5 h-5 text-slate-400" />
                Benachrichtigungen
              </h2>
              <div className="space-y-4">{selectConfigs.map(renderLimitCard)}</div>
            </div>
          )}

          {/* Sonderfunktionen */}
          {numberOrOffConfigs.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Baby className="w-5 h-5 text-slate-400" />
                Sonderfunktionen
              </h2>
              <div className="space-y-4">{numberOrOffConfigs.map(renderLimitCard)}</div>
            </div>
          )}

          {/* Baby Care Koordinator */}
          <div>
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Baby className="w-5 h-5 text-pink-400" />
              Baby-Care-Koordinator
            </h2>
            {renderBabyCareCard()}
          </div>
        </div>
      )}

      {/* Info */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-blue-800">Hinweis</h3>
            <p className="text-sm text-blue-700 mt-1">
              Änderungen werden sofort für alle Nutzer wirksam. Der Subscription-Tier eines Nutzers
              bestimmt, welche Limits angewendet werden. Beta-Tester erhalten automatisch
              Premium-Limits.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
